// React Dependencies
import React, { useState, useReducer, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Internal Dependencies
import FaqModal from './FaqModal';
import MText from '../../../common/MText';
import MButton from '../../../common/MButton';
import OverLoading from '../../../global/OverLoading';
import ApiService from '../../../../services.js/apiService';
import Colours from '../../../../styles/colours';
import FaqItem from './FaqItem';
import { IsEmpty } from '../../../../common/checks';
import { Status } from '../../../../common/constants';

const defaultModalState = { open: false, id: null, qns: '', answer: '', isDeleting: false };

const Faq = ({ match }) => {
  const { pageId, loyaltyCardId } = match.params;

  const [faqs, setFaqs] = useState([]);
  const [status, setStatus] = useState(Status.Deactivated);
  const [isLoading, setIsLoading] = useState(true);

  const [modal, setModal] = useReducer(
    (state, action) => ({ ...state, ...action }),
    defaultModalState,
  );

  const isDeactivated = status === Status.Deactivated;

  const getLoyaltyFaqs = async () => {
    try {
      const res = await ApiService.getLoyaltyFaqs(pageId, loyaltyCardId);
      setFaqs(res.faqs);
      setStatus(res.loyaltyCard.status);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setIsLoading(true);
      const { qns, answer } = modal;
      await ApiService.createLoyaltyFaq(pageId, loyaltyCardId, { qns, answer });
      setModal({ open: false });
      await getLoyaltyFaqs();
      toast.success('FAQ has been successfully created');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setIsLoading(true);
      const { qns, answer, id: faqId } = modal;
      await ApiService.updateLoyaltyFaq(pageId, loyaltyCardId, faqId, { qns, answer });
      setModal({ open: false });
      await getLoyaltyFaqs();
      toast.success('FAQ has been successfully updated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const { id: faqId } = modal;
      await ApiService.deleteLoyaltyFaq(pageId, loyaltyCardId, faqId);
      setModal({ open: false });
      await getLoyaltyFaqs();
      toast.success('FAQ has been successfully deleted');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReorder = async (event) => {
    try {
      setIsLoading(true);
      const { active, over } = event;
      if (!IsEmpty(over) && active.id !== over.id) {
        const oldIndex = faqs.findIndex((item) => item.id === active.id);
        const newIndex = faqs.findIndex((item) => item.id === over.id);
        const reorderedFaqs = arrayMove(faqs, oldIndex, newIndex).map((faq, i) => ({ ...faq, index: i + 1 }));
        setFaqs(reorderedFaqs);

        await ApiService.updateLoyaltyFaqOrder(
          pageId,
          loyaltyCardId,
          { faqIds: reorderedFaqs.map((faq) => faq.id) },
        );
        toast.success('FAQ order has been successfully updated');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getLoyaltyFaqs();
  }, []);

  return (
    <div className="container-fluid">
      {isLoading && <OverLoading />}
      <div className="row mb-3">
        <div className="col-12 pl-0 d-flex align-items-end">
          <div>
            <MText size={12} colour={Colours.DarkGray} className="mb-2" text={`Loyalty Card ID: ${match.params.loyaltyCardId}`} />
            <div className="d-flex align-items-center">
              <MText bold size={16} colour={Colours.Gray} className="mr-4" text="FAQ Settings" />
              {!isDeactivated && (
                <MButton
                  textSize={12}
                  className="py-2"
                  colour={Colours.MaterialBlue}
                  textStyle={{ fontWeight: 500 }}
                  textColour={Colours.White}
                  onClick={() => setModal({ ...defaultModalState, open: true })}
                  text="+ ADD"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 pt-3 px-3 rounded" style={{ backgroundColor: Colours.LightGray1 }}>
          <div className="container-fluid">
            <DndContext onDragEnd={handleReorder} modifiers={[restrictToVerticalAxis]}>
              <SortableContext items={faqs} strategy={verticalListSortingStrategy}>
                {faqs.map((faq) => (
                  <FaqItem
                    key={faq.id}
                    faq={faq}
                    disabled={isDeactivated}
                    onUpdate={() => setModal({ open: true, id: faq.id, qns: faq.qns, answer: faq.answer, isDeleting: false })}
                    onDelete={() => setModal({ open: true, id: faq.id, qns: faq.qns, answer: faq.answer, isDeleting: true })}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>

      <FaqModal
        modal={modal}
        handleValueUpdate={(value) => setModal(value)}
        handleClose={() => setModal({ open: false })}
        handleUpdate={handleUpdate}
        handleDelete={handleDelete}
        handleAdd={handleAdd}
      />
    </div>
  );
};

Faq.propTypes = {
  match: PropTypes.shape(Object).isRequired,
};

export default Faq;
